/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const productState = getProductStateByPath(state, productPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);

    return {
        productState: computedVariantProduct,
    };
};

const mapDispatchToProps = {};

const enum fields {
    headLine = "headLine",
    candidId = "candidId",
    containerId = "containerId",
    theme = "theme",
    overlayTheme = "overlayTheme",
    cluster = "cluster",
    margin = "margin",
    width = "width",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headLine]: string;
        [fields.candidId]: string;
        [fields.containerId]: string;
        [fields.theme]: string;
        [fields.overlayTheme]: string;
        [fields.cluster]: string;
        [fields.margin]: number;
        [fields.width]: number;
        [fields.customCSS]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const SurCanidiWidget: React.FunctionComponent<Props> = props => {
    const [isCandidDisplay, setIsCandidDisplay] = React.useState(true);
    const [showtitle, setShowTitle] = React.useState(false);

    const emptyFunction = () => {
        setIsCandidDisplay(false);
    };
    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "//api.getcandid.com/scripts/widget.js";
        document.body.appendChild(script);
        script.addEventListener("load", () => {
            if ((window as any).candid !== undefined) {
                (window as any).candid.init({
                    id: props.fields.candidId,
                    tag: props.productState.value?.productTitle,
                    containerId: props.fields.containerId,
                    theme: props.fields.theme,
                    overlayTheme: props.fields.overlayTheme,
                    cluster: props.fields.cluster,
                    margin: props.fields.margin,
                    controlNav:false,
                    width: props.fields.width,
                    slideshow: true,
                    sort: "date",
                    sortDirection: "Descending",
                    hideIfEmpty: true,
                    // eslint-disable-next-line object-shorthand
                    ready: function () {
                        setShowTitle(true);
                        setIsCandidDisplay(true);
                        
                    },
                    // eslint-disable-next-line object-shorthand
                    empty: function () {
                        setShowTitle(false);
                        setIsCandidDisplay(false);
                    },
                });
            }
        });

        return () => {
            document.body.removeChild(script);
        };
    }, []);
    return (
        <>
            {isCandidDisplay && (
                <StyledWrapper {...(showtitle && customCssWrapper)}>
                    {showtitle && (
                        <StyledWrapper>
                            <Typography
                                css={css`
                                    line-height: 38px;
                                    font-size: 28px;
                                    font-weight: 600;
                                    display: block;
                                    text-align: center;
                                    padding-top: 25px;
                                    padding-bottom: 25px;
                                `}
                            >
                                {props.fields.headLine}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <div id="candid-container"></div>
                </StyledWrapper>
            )}
        </>
    );
};

const defaultCustomCss = `& {
}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SurCanidiWidget),
    definition: {
        group: "Basic",
        icon: "Rtf",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.headLine,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.candidId,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.containerId,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.theme,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.overlayTheme,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.cluster,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.margin,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.width,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
